import { DeclarationStatuses } from 'store/declarations/enums/common/declaration-status';
import { ICS2MessageTypes } from 'store/declarations/enums/common/declaration-types';
import { Customer } from 'store/customers/customer';
import { Individual } from 'store/individuals/individual';
import { JobResponse } from 'store/jobs/job';
import { SecurityCheckStatus } from 'types/common';
import { SortDirection } from 'utils/tableHelpers';
import { TransportType } from 'utils/transportTypes';
import { ContainerStatuses } from 'views/manifests/components/create-manifest-form/types';
import { ManifestStatus } from 'views/manifests/types/manifest-statuses';

export type CreateManifestPayload = {
    referenceId: string;
    haulier?: string | null;
    teamId: string;
    departurePort?: string | null;
    declarationsData: {
        transportId: TransportType;
        departureCountryId: string;
        arrivalPort: string;
        meansIdentificationType: string;
        meansOfTransportOnArrivalIdNumber: string;
        nationalityTransportMeans: string;
        vehicleRegistration?: string | null | undefined;
        dateTimeOfDeparture?: string | null;
        estimatedDateTimeOfArrival: string;
        containerIndicator: ContainerStatuses;
    };
};

export type UpdateManifestPayload = {
    haulier?: string | null;
    referenceId: string;
    departurePort?: string | null;
    declarationsData: {
        transportId: TransportType;
        departureCountryId: string;
        arrivalPort: string;
        meansIdentificationType: string;
        meansOfTransportOnArrivalIdNumber: string;
        nationalityTransportMeans: string;
        vehicleRegistration?: string | null | undefined;
        dateTimeOfDeparture?: string | null;
        estimatedDateTimeOfArrival: string;
        containerIndicator: ContainerStatuses;
    };
    amendmentReason?: string;
};

export enum GetManifestsSortParams {
    ID = 'ID',
    REFERENCE_ID = 'REFERENCE_ID',
    OWNER_ID = 'OWNER_ID',
    TEAM_ID = 'TEAM_ID',
    ENS_REFERENCE = 'ENS_REFERENCE',
    PBN = 'PBN',
    GMR = 'GMR',
    STATUS = 'STATUS',
    CREATED_BY = 'CREATED_BY',
    UPDATED_AT = 'UPDATED_AT',
    CREATED_AT = 'CREATED_AT',
}

export type GetManifestsParams = Partial<{
    sortDirection: SortDirection;
    sort: GetManifestsSortParams;
    referenceId: string;
    teamId: string;
    ensReference: string;
    pbn: string;
    manifestStatuses: ManifestStatus[];
    createdBy: string; //individual id
    ownerId: string; // assigned to (individual id)
    archived: boolean;
    page: number;
    size: number;
    query: string;
    exactDate: string;
    arrivalEligible: boolean;
}>;

export type Manifest = {
    id: string;
    referenceId: string;
    assigneeId: string;
    owner?: Individual;
    ensReference: string | undefined;
    pbn: string | undefined;
    gmr: string | undefined;
    cdsExportMRN: string | undefined;
    status: ManifestStatus;
    haulier?: string | null;
    departurePort?: string;
    declarationsData: {
        transportId: TransportType; // TransportType
        meansIdentificationType: string;
        meansOfTransportOnArrivalIdNumber: string;
        nationalityTransportMeans: string;
        vehicleRegistration?: string | undefined;
        departureCountryId: string;
        arrivalPort: string;
        dateTimeOfDeparture?: string | null;
        estimatedDateTimeOfArrival: string;
        containerIndicator: boolean;
    };
    team?: {
        id: string;
        name: string;
    };
    jobs: JobResponse[];
    documents: string[];
    brokerId: string;
    createdBy: string;
    updatedAt: string;
    createdAt: string;
    archived: boolean;
    creator: Partial<Individual>;
    transportDetailsMismatch?: boolean;
};

export type CreateManifestResponse = {
    category: string;
    code: string;
    message: string;
    payload: Manifest;
};

export type GetManifestsResponse = {
    payload: {
        list: Manifest[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
};

export type ManifestResponse = {
    category: string;
    code: string;
    message: string;
    payload: Manifest;
};

export type GetManifestsByIdPayload = {
    manifestId: string;
};

export type UpdateManifestResponse = {
    payload: Manifest;
    category: string;
    code: string;
    message: string;
};

export type ManifestNote = {
    id: string;
    manifestId: string;
    title: string;
    note: string;
    creator: Individual;
    createdAt: string;
    updatedAt: string;
};

export type AddNoteToManifestPayload = {
    note: string;
};

export type CreateUpdateManifestNoteResponse = {
    category: string;
    code: string;
    message: string;
    payload: ManifestNote;
};

export type GetNotesForManifestParams = {
    sortDirection?: SortDirection;
    page?: number;
    size?: number;
};

export type GetNotesForManifestResponse = {
    payload: {
        list: ManifestNote[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
};

export type AddRemoveJobFromManifestPayload = {
    manifestId: string;
    jobId: string;
};

export type UpdateManifestExternalReferencesPayload = {
    ensReference?: string | null;
    pbn?: string | null;
    gmr?: string | null;
};

export type GetManifestHistoryParams = Partial<{
    sortDirection: SortDirection;
    page: number;
    size: number;
    startDate?: string;
    endDate?: string;
}>;

export enum ManifestHistoryTypes {
    CREATED = 'CREATED',
    UPDATED = 'UPDATED',
    ARCHIVED = 'ARCHIVED',
    UNARCHIVED = 'UNARCHIVED',
    ASSIGNED_TO_OWNER = 'ASSIGNED_TO_OWNER',
    ASSIGNED_TO_TEAM = 'ASSIGNED_TO_TEAM',
    JOB_ADDED = 'JOB_ADDED',
    JOB_REMOVED = 'JOB_REMOVED',
    EXTERNAL_REFERENCES_UPDATED = 'EXTERNAL_REFERENCES_UPDATED',
}

export type ManifestHistory = {
    id: string;
    manifestId: string;
    type: ManifestHistoryTypes;
    createdAt: string;
    creator: Individual;
};

export type GetManifestHistoryResponse = {
    payload: {
        list: ManifestHistory[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
};

export type DeleteDocumentResponse = {
    category: string;
    code: string;
    message: string;
    payload: string;
};

export type GetDocumentPresignedUrlData = {
    declarationId: string;
    customerId: string;
    preSignedUrl: string;
    operationType: 'DOWNLOAD' | 'UPLOAD';
};

export type GetDocumentPresignedUrlResponse = {
    category: string;
    code: string;
    message: string;
    payload: GetDocumentPresignedUrlData;
};

export type GetDocumentUploadUrlParams = {
    documentName: string;
    documentSize: number;
};

export type ManifestDocument = {
    id: string;
    brokerId: string;
    link: string;
    contentType: string;
    fileName: string;
    fileSize: number;
    securityChecksStatus: SecurityCheckStatus;
    createdAt: string;
    creator: {
        id: string;
        fullName: string;
        address: {
            addressLine1: string;
            addressLine2: string;
            postCode: string;
            city: string;
            country: string;
        };
        phone: string;
        type: string;
        email: string;
        customerId: string;
        preferredJobType: string;
        vehicleType: string;
        vehicleRegistrationNumber: string;
        createdAt: string;
        hasHmrcToken: boolean;
        referenceNumber: string;
        profilePictureLink: string;
        profilePictureStatus: string;
    };
    updatedAt: string;
    archived: boolean;
    archivedAt: string;
};

export type CreateDocumentResponse = {
    category: string;
    code: string;
    message: string;
    payload: ManifestDocument;
};

export type ListDocumentsParams = Partial<{
    sortDirection: SortDirection;
    fileSortParameter: 'ID' | 'FILE_SIZE' | 'CONTENT_TYPE' | 'FILE_NAME' | 'CREATED_AT' | 'UPDATED_AT';
    page: number;
    size: number;
    fileName: string;
    contentType: string[];
    creator: string;
    archived: boolean;
}>;

export type ListDocumentsResponse = {
    payload: {
        list: ManifestDocument[];
        pageNumber: number;
        pageSize: number;
        total: number;
    };
    category: string;
    code: string;
    message: string;
};

export type ManifestDeclarationResponse = {
    declarationId: string;
    mrn: string | null;
    status: DeclarationStatuses;
};

export type ManifestDeclarationsResponse = {
    jobId: string;
    jobReferenceId: string;
    declarations: ManifestDeclarationResponse[];
};

export type CreateIcs2EnsDeclarationsParams = {
    ics2Type: ICS2MessageTypes;
    entryTransportType: string;
    templateId?: string | null;
};

/**
 * One ICS2 ENS declaration generated from a manifest's AIS H1 declarations. F11 yields a single
 * declaration covering every job, F50 yields one per job.
 */
export type Ics2EnsDeclarationResponse = {
    declarationId: string;
    jobId: string;
    ics2Type: ICS2MessageTypes;
    houseConsignments: number;
    goodsItems: number;
};

export type CreateThirdPartyJobPayload = {
    customerId: string | undefined;
    jobReferenceId: string | undefined;
    mrns: string[];
    customsBrokerName?: string;
    customsBrokerEmail?: string;
    customsBrokerPhoneCountryCode?: string;
    customsBrokerPhoneNumber?: string;
};

export type ThirdPartyJobResponse = {
    id: string;
    manifestId: string;
    customerId: string;
    customer: Customer;
    jobReferenceId: string;
    mrns: string[];
    customsBrokerName?: string;
    customsBrokerEmail?: string;
    customsBrokerPhoneCountryCode?: string;
    customsBrokerPhoneNumber?: string;
    createdAt: string;
    updatedAt: string;
};

export type ListManifestEnsDeclarationsParams = {
    page?: number;
    size?: number;
    query?: string;
    exactDate?: string;
    archived?: boolean;
};
